import React, { memo, useRef, useEffect, useCallback, useState} from 'react';
import {pt_dist} from "effects/midi/mappable/convexHull.js";
import {intersect} from './intersect.js';
import {mod} from 'utils/mod.js';
import {drawContours} from "components/contour/drawContours.js";
import {connect} from 'react-redux';
import Colors from 'components/Colors.js';

const colors = new Colors();

const factor = window.factor || 2; //(total - nth) / total;

const DEFAULT_WIDTH = 280;
const DEFAULT_HEIGHT = 280;

const Structure = memo(({
    leafDetails,
    WIDTH=DEFAULT_WIDTH,
    HEIGHT=DEFAULT_HEIGHT,
    setLeafDetails,
    FULL_RADIUS = (WIDTH/2)*.95,
    isScript,
    backgroundColor,
    color,
    tidals,
    fillColor,
    dotColor,
    radius=FULL_RADIUS,
    ...props
}) => { 

    tidals = tidals.filter(x => x.inUse || x.lastUsed < 1);
    let total = tidals.length;

    let ref = useRef(undefined);
    let leaves = useRef([]);
    let ctxt = useRef(undefined);

    useEffect(() => {
        ctxt.current = ref.current.getContext('2d');
        draw();
    }, []);

    useEffect(() => {
        if (ctxt.current) {
            ctxt.current.scale(window.devicePixelRatio, window.devicePixelRatio);
        }
    }, [WIDTH, HEIGHT]);

    let center = WIDTH/2;

    useEffect(() => {
        if (ctxt.current) {
            if (tidals.length === 6 || isScript) {
                window.requestAnimationFrame(draw);
            }
        }
    }, [props, center, dotColor, leafDetails, tidals, fillColor, WIDTH, HEIGHT, radius]);


    const draw = useCallback(() => {
        let c = ctxt.current;
        c.clearRect(0,0,WIDTH,HEIGHT);
        leaves.current = [];
        let nextRadius = undefined;
        for (let nth=0; nth < tidals.length; nth++) {
            let tidal = tidals[nth];
            let cycle = tidal.cycle;
            let RADIUS = nextRadius || Math.max(10, radius - 12*factor*(nth));

            let geoColor = "#0000" + 2*nth + nth;
            if (nth === 0) {
                c.beginPath();
                
                // c.strokeStyle = color;
                c.arc(center, center, RADIUS, 0, 2*Math.PI);
                c.fillStyle = backgroundColor;
                c.fill();
                //c.stroke();
                c.closePath();
            }

            if (props.sequencerEngine && !props.sequencerEngine.tracks[nth]) {
                continue;
            }

            let flattened = cycle; 
            let len = flattened.length;
            for (let i=0; i < flattened.length; i++) {
                let leaf = flattened[i];
                if (Array.isArray(leaf)) {
                    leaf = leaf[0];
                }
                let track = props.sequencerEngine && props.sequencerEngine.tracks[nth];
                if (!props.sequencerEngine || track) {
                    let _nextRadius = drawConnector(c, i, len, cycle, RADIUS, {x:center, y:center}, leaf);
                    if (_nextRadius) {
                        nextRadius = _nextRadius;
                    }
                }

                if (leaf.type && leaf.type.type === '-' && leaf.type.value === 0) {
                    if (true || leaf.transpose !== null) {
                        drawDash(c, leaf, i, RADIUS, cycle, len);
                   }
                }
                //if (window.drawDot) {
                drawDot(c, leaf, i, RADIUS, cycle, nth, len, tidal);
                //}
           }
        }
    }, [
        color,
        center,
        backgroundColor,
        tidals,
        total,
        leafDetails,
        fillColor,
        radius,
        WIDTH,
        HEIGHT]);

    const drawConnector = useCallback((c, a, len, cycle, radius, _center={x: center, y: center}, leaf, depth=0) => {
        if (leaf.transpose === null) {
            return;
        }
        let c0 = getLeafCoord(a, _center, radius, cycle);
        let c1 = getLeafCoord((a+1)%len, _center, radius, cycle);
        let c2 = getLeafCoord((a+2)%len, _center, radius, cycle);
        let c3 = getLeafCoord((a+3)%len, _center, radius, cycle);
        let c5 = getLeafCoord(mod(a-3,len), _center, radius, cycle);
        let c6 = getLeafCoord(mod(a-2,len), _center, radius, cycle);
        let c7 = getLeafCoord(mod(a-1,len), _center, radius, cycle);

        let p0 = c0;
        let p1 = intersect(c0, c3, c1, c6);
        let p2 = intersect(c7, c2, c6, c1);
        let p3 = intersect(c0, c5, c2, c7);

        c.beginPath();
        c.lineWidth= 1;
        c.moveTo(p0.x, p0.y);
        c.lineTo(p1.x, p1.y);
        c.lineTo(p2.x, p2.y);
        c.lineTo(p3.x, p3.y);
        c.closePath();
        c.fillStyle = fillColor; //fillColor; //leaf.transpose === null ? fillColor : "white";
        c.fill();
        c.strokeStyle = color; //leaf.transpose === null ? color : "white";
        c.stroke();

        return pt_dist({x: center, y: center}, p2);
    }, [center, radius, WIDTH, HEIGHT, fillColor, color]);

    const drawDash = useCallback((c, leaf, a, radius, cycle, len) => {
        // draw arc
        let _center = {x: center, y: center};
        let c0 = getLeafCoord(a, _center, radius, cycle);
        let c1 = getLeafCoord((a+1)%len, _center, radius, cycle);
        let c2 = getLeafCoord((a+2)%len, _center, radius, cycle);
        let c3 = getLeafCoord((a+3)%len, _center, radius, cycle);
        let c5 = getLeafCoord(mod(a-3,len), _center, radius, cycle);
        let c6 = getLeafCoord(mod(a-2,len), _center, radius, cycle);
        let c7 = getLeafCoord(mod(a-1,len), _center, radius, cycle);

        //let p1 = intersect(c0, c3, c2, c7);
        let p3 = intersect(c0, c3, c1, c6);

        c.lineWidth = 1;
        c.beginPath();
        c.moveTo(c0.x, c0.y);
        c.lineTo(p3.x, p3.y);
        c.lineTo(c1.x, c1.y);
        c.closePath();
        c.fillStyle = fillColor; //fillColor; //leaf.transpose === null ? fillColor : "white";
        c.fill();
        c.strokeStyle = color; //leaf.transpose === null ? color : "white";
        c.stroke();

        /*
        if (p3) {
            drawContours(c0,c1,p3,c, 5, fillColor);
        }
        */

        /*
        c.beginPath();
        c.lineWidth = factor*2;
        c.strokeStyle = color;
        c.arc(center,center,RADIUS,c1.deg, c2.deg, false);
        c.stroke();
        c.closePath();
        */
    }, [center, radius, HEIGHT, WIDTH, fillColor, color]);

    const drawDot = (c, leaf, i, RADIUS, cycle, nth, len, tidal) => {
        return;
        let {deg,x,y} = getLeafCoord(i, {x:center,y:center}, RADIUS, cycle);
        let path = new Path2D();
        let {fill, stroke,radius}= getLeafStyle(leaf, (len - nth)/len, nth);
        if  (leaf.transpose === null ) {
            return;
        }
        path.arc(x,y,0.45*factor*radius,0,2*Math.PI);
        leaves.current.push({leaf, path, nth});
        c.lineWidth = 2;
        if (leafDetails === leaf) {
            fill = "white";
        }
        c.fillStyle = fill;
        c.fill(path);
        if (stroke) {
            c.strokeStyle = stroke;
            c.stroke(path);
        }
        
        if (leaf.accent) {
            let path = new Path2D();
            path.arc(x,y,factor*radius,0,2*Math.PI);
            c.fillStyle = colors.transparent(colors.saturate(fill, 3), 0.6); // "#ffffff45";
            c.fill(path);
        }

        if (leaf.legato) {
            let path = new Path2D();
            path.arc(x,y,factor*radius,0,2*Math.PI);
            c.fillStyle = fill;
            c.fill(path);
        }
        
        if (leaf.staccato) {
            // then we need to add a square to center
            let w = (radius*0.3)*factor;
            c.fillStyle=backgroundColor;
            c.fillRect(x-w*0.5,y-w*0.5,w,w);
        }

        if (!leaf.piano && !leaf.pianissimo) {
            let circle = new Path2D();
            circle.arc(x,y,radius,0,2*Math.PI);
            c.strokeStyle = color;
            c.stroke(circle);
        }

    };

    const getLeafStyle = useCallback((leaf, ratio, nth) => {
        let _color = dotColor; 

        let radius = 5*ratio; 
        if (leaf.transpose === null) {
            return {fill: colors.transparent(colors.saturate(backgroundColor, 1)), stroke: _color, radius};
        } else {
            return {fill: _color, radius};
        }
    }, [dotColor, backgroundColor, radius]); 

    const getLeafCoord = useCallback((i, center, RADIUS, cycle) => {
        i *= 1.0;
        let flattened = cycle.flat(16);
        let len = (cycle.length);
        let deg = (2*Math.PI * i/len) - 0.5*Math.PI;
        let x = center.x + RADIUS * Math.cos(deg);
        let y = center.y + RADIUS * Math.sin(deg);
        return {x,y,deg};
    }, [center, radius, WIDTH, HEIGHT]);

    const onMouseDown = useCallback((e) => {
        let rect = ref.current.getBoundingClientRect();
        let x = Math.round((e.clientX - rect.left) * 2);
        let y = Math.round((e.clientY - rect.top) * 2);
        for (let {leaf, path} of leaves.current) {
            if (ctxt.current.isPointInPath(path, x, y)) {
                setLeafDetails(leaf);
                break;
            }
        }
    }, [setLeafDetails]);

    return (
        <canvas
          onMouseDown={onMouseDown}
          className="tidal-art structure"
          style={{
              width: WIDTH,
              height: HEIGHT
          }}
          width={WIDTH*window.devicePixelRatio}
          height={HEIGHT*window.devicePixelRatio}
          ref={ref}>
        </canvas>);
});

const mapStateToProps = (state, props) => {
    let x = {
    };
    props.tidals.forEach(
        t => {
            x["cycle" + t.id] = t.cycle;
        });
    return x;
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(Structure);
